// pages/trading-portal/TradeExecution/MarketDataSections.js
// The blackboard snapshot rendered as titled sections of category cards: the Live Market Data
// tab, the Prompt Editor sidebar, the Decision Log snapshot and both simulators' bar inspectors
// (the Data Simulator tab and the AI Agent Simulator page). Moved out of AIAgentPage.js unchanged.

import React from 'react';
import { Box, Grid, Paper, Tooltip, Typography } from '@mui/material';
import * as MuiIcons from '@mui/icons-material';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

// ==================== LIVE MARKET DATA — SHARED CATEGORY RENDERING ====================
// The Live Market Data tab and the Prompt Editor sidebar panel show the exact
// same snapshot, so the grouping rules live here once. Keeping them module-scope
// (like CandleChart) also stops the rows from remounting on every SSE tick /
// Decision-Timing poll, which re-renders the whole page.
//
// `agent` is the config_json.enabled_agents key that gates the section — a
// disabled strategist's cards are hidden because the LLM never receives them.
const MARKET_DATA_SECTIONS = [
    {
        key: 'fed_macro', title: 'Fed Macro Liquidity', color: '#059669',
        agent: 'macro_strategist',
        categoryKeys: ['current_fed_macro', 'prev_fed_macro', 'macro_strategist_agent'],
    },
    {
        key: 'options_desk', title: 'Options & Volatility Desk', color: '#8b5cf6',
        agent: 'options_volatility',
        categoryKeys: ['options_data', 'strangle_geometry', 'options_volatility_agent'],
    },
    {
        key: 'crypto_divergence', title: 'The Crypto / Divergence Strategist', color: '#f59e0b',
        agent: 'asset_strategist',
        categoryKeys: ['asset_classification', 'crypto_arbitrage', 'asset_strategist_agent'],
    },
    {
        key: 'vwap_execution', title: 'VWAP Execution Trader', color: '#0ea5e9',
        agent: null,
        categoryKeys: ['technical_data', 'order_flow', 'execution_gates', 'vwap_execution_agent'],
    },
];

// Every category key claimed by a named section. Anything the backend adds
// later falls through to the trailing untitled "other" group automatically.
const SECTIONED_CATEGORY_KEYS = MARKET_DATA_SECTIONS.reduce(
    (acc, s) => acc.concat(s.categoryKeys), [],
);

export const resolveEnabledAgents = (agent) => ({
    macro_strategist: agent?.config_json?.enabled_agents?.macro_strategist ?? true,
    options_volatility: agent?.config_json?.enabled_agents?.options_volatility ?? true,
    asset_strategist: agent?.config_json?.enabled_agents?.asset_strategist ?? true,
});

// -> [{ key, title, color, entries: [[categoryKey, category], ...] }] in render
// order, with a trailing { key: 'other', title: null } group. Sections with no
// present categories are dropped so neither surface renders an empty heading.
export const buildMarketDataSections = (categories, enabledAgents) => {
    const cats = categories || {};
    const sections = MARKET_DATA_SECTIONS
        .filter((s) => !s.agent || enabledAgents[s.agent])
        .map((s) => ({
            key: s.key,
            title: s.title,
            color: s.color,
            entries: s.categoryKeys.map((k) => [k, cats[k]]).filter(([, cat]) => cat),
        }))
        .filter((s) => s.entries.length > 0);

    // Note: keys of a DISABLED section stay in SECTIONED_CATEGORY_KEYS, so they
    // are excluded here too rather than resurfacing under "other".
    const otherEntries = Object.entries(cats)
        .filter(([k]) => !SECTIONED_CATEGORY_KEYS.includes(k));
    if (otherEntries.length > 0) {
        sections.push({ key: 'other', title: null, color: null, entries: otherEntries });
    }
    return sections;
};

// Does this value render as N/A? Pulled out of DataFieldRow alongside
// formatFieldValue so the Variables panel can grey out missing values the same
// way the data cards do.
export const isNAFieldValue = (value, format) => (
    value === 'N/A' || value === null || value === undefined
    || (typeof value === 'string' && value.includes('N/A'))
    || (typeof value === 'number' && value === 0 && format === 'price')
);

// Value -> display string. Extracted from DataFieldRow so the Variables panel
// chips show exactly what the data cards show — one implementation, so adding a
// backend `format` cannot make the two surfaces disagree.
export const formatFieldValue = (value, format) => {
    if (isNAFieldValue(value, format)) {
        return typeof value === 'string' && value.includes('N/A') ? value : 'N/A';
    }
    if (typeof value === 'boolean') return value ? 'TRUE' : 'FALSE';
    if ((format === 'price' || format === 'currency') && typeof value === 'number') {
        return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (format === 'large_number' && typeof value === 'number') {
        return value >= 1e12 ? `$${(value / 1e12).toFixed(2)}T`
            : value >= 1e9 ? `$${(value / 1e9).toFixed(2)}B`
            : value >= 1e6 ? `$${(value / 1e6).toFixed(2)}M`
            : `$${value.toLocaleString()}`;
    }
    if (format === 'percent' && typeof value === 'number') {
        return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
    }
    if (format === 'number' && typeof value === 'number') return value.toFixed(4);
    if (format === 'integer' && typeof value === 'number') return value.toString();
    return value;
};

// `name` -> { value, format } for every field the backend categorised, so the
// Variables panel can label each chip with its live value using the SAME format
// metadata the data cards use. Category fields win; the raw blackboard fills in
// the variables no category claims (timestamp, symbol, recent_candles, ...).
export const buildVariableValueLookup = (marketData) => {
    const out = {};
    Object.values(marketData?.categories || {}).forEach((cat) => {
        Object.entries(cat?.fields || {}).forEach(([k, f]) => {
            out[k] = { value: f.value, format: f.format };
        });
    });
    Object.entries(marketData?.raw || {}).forEach(([k, v]) => {
        if (!(k in out)) out[k] = { value: v, format: undefined };
    });
    return out;
};

// The backend splits the candle text into two prompt variables — `recent_candles`
// (the agent's Timeframe) and `recent_candles_30s` (the 30-second tail). The Live
// Market Data "Recent Candles" panel is a single combined block, so it re-joins
// them here. `recent_candles` carries no trailing newline and the 30s block opens
// on its own `Recent 5 Minutes (THIRTY_SEC):` header, so a '\n' join reproduces
// the original single string byte-for-byte.
const NO_CANDLES_TEXT = 'No candle data available';
const NO_CANDLES_30S_TEXT = 'No 30-second candle data available';
export const joinCandleText = (md) => [md?.recent_candles, md?.recent_candles_30s]
    .filter((t) => t && t !== NO_CANDLES_TEXT && t !== NO_CANDLES_30S_TEXT)
    .join('\n');

// Chip-sized rendering of a variable's value. Long strings (recent_candles is
// a multi-line block) are clipped — the full value stays in the tooltip.
const VARIABLE_VALUE_MAX_CHARS = 16;
export const compactVariableValue = (entry) => {
    if (!entry) return null;
    const formatted = formatFieldValue(entry.value, entry.format);
    if (formatted === null || formatted === undefined) return null;
    const flat = String(formatted).replace(/\s+/g, ' ').trim();
    if (!flat) return null;
    return flat.length > VARIABLE_VALUE_MAX_CHARS
        ? `${flat.slice(0, VARIABLE_VALUE_MAX_CHARS)}…`
        : flat;
};

// One label/value row. `dense` is the Prompt Editor sidebar variant: smaller
// type and a wrapping label so long gate names fit a 320px column.
export const DataFieldRow = ({ label, value, format, naReason, dense = false }) => {
    const isNA = isNAFieldValue(value, format);
    const isZero = typeof value === 'number' && value === 0;
    const displayValue = formatFieldValue(value, format);

    return (
        <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
            py: dense ? 0.35 : 0.8, px: dense ? 0.75 : 1.5,
            '&:hover': { bgcolor: '#f8fafc' }, borderRadius: '6px',
        }}>
            <Typography sx={{ fontFamily, fontSize: dense ? '0.66rem' : '0.82rem', color: '#64748b', lineHeight: 1.35 }}>{label}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                <Typography sx={{
                    fontFamily, fontSize: dense ? '0.68rem' : '0.85rem', fontWeight: 600, textAlign: 'right',
                    color: isNA ? '#ef4444' : isZero ? '#94a3b8' : (format === 'text' && value === 'GREEN') ? '#10b981' : (format === 'text' && value === 'RED') ? '#ef4444' : '#1e293b',
                }}>
                    {displayValue}
                </Typography>
                {isNA && naReason && (
                    <Tooltip
                        title={naReason}
                        placement="left"
                        arrow
                        componentsProps={{
                            tooltip: { sx: { fontFamily, fontSize: '0.72rem', maxWidth: 320, lineHeight: 1.4 } },
                        }}
                    >
                        <MuiIcons.InfoOutlined sx={{ fontSize: 14, color: '#94a3b8', cursor: 'help' }} />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );
};

// Single-column rendering of the same sections the Live Market Data tab lays
// out as a card grid — for the Prompt Editor sidebar, where there is no room
// for the grid but the author still needs to see every field the prompt can
// reference.
export const CompactMarketDataSections = ({ sections }) => (
    <>
        {sections.map((section) => (
            <Box key={section.key} sx={{ mb: 1 }}>
                {section.title && (
                    <Typography sx={{
                        fontFamily, fontWeight: 700, fontSize: '0.6rem', color: '#475569',
                        textTransform: 'uppercase', letterSpacing: '0.04em',
                        borderLeft: `3px solid ${section.color}`, pl: 0.75, mb: 0.5,
                    }}>
                        {section.title}
                    </Typography>
                )}
                {section.entries.map(([catKey, category]) => (
                    <Box key={catKey} sx={{ mb: 0.5, border: '1px solid #e2e8f0', borderRadius: '6px', bgcolor: '#fff', overflow: 'hidden' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 0.75, py: 0.4, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <Typography sx={{ fontFamily, fontSize: '0.62rem', fontWeight: 700, color: '#334155' }}>
                                {category.label}
                            </Typography>
                            <Typography sx={{ fontFamily, fontSize: '0.55rem', color: category.last_updated ? '#94a3b8' : '#ef4444', ml: 'auto', whiteSpace: 'nowrap' }}>
                                {category.last_updated ? new Date(category.last_updated).toLocaleTimeString() : 'Never'}
                            </Typography>
                        </Box>
                        <Box sx={{ px: 0.25, py: 0.25 }}>
                            {Object.entries(category.fields || {}).map(([fKey, field]) => (
                                <DataFieldRow key={fKey} dense label={field.label} value={field.value} format={field.format} naReason={field.na_reason} />
                            ))}
                        </Box>
                    </Box>
                ))}
            </Box>
        ))}
    </>
);

// Card-grid rendering of the shared sections. The Live Market Data tab and
// the Decision Log snapshot dialog both render through this, so the two
// cannot drift. Module scope for the same reason DataFieldRow is: it must
// not remount on every SSE tick.
export const MarketDataSectionGrid = ({ sections }) => {
    const renderCategoryCard = ([key, category]) => {
        const fields = category.fields || {};
        const allNA = Object.values(fields).every(f =>
            f.value === 'N/A' || f.value === null || f.value === undefined
            || (typeof f.value === 'string' && f.value.includes('N/A'))
        );
        const hasData = Object.values(fields).some(f =>
            f.value !== null && f.value !== undefined && f.value !== 0
            && f.value !== 'N/A' && !(typeof f.value === 'string' && f.value.includes('N/A'))
        );

        return (
            <Grid item xs={12} md={6} lg={4} key={key}>
                <Paper sx={{
                    borderRadius: '12px',
                    border: `1px solid ${allNA ? '#fecaca' : hasData ? '#bbf7d0' : '#e2e8f0'}`,
                    boxShadow: 'none', overflow: 'hidden', height: '100%',
                }}>
                    <Box sx={{
                        px: 2, py: 1.2, display: 'flex', alignItems: 'center', gap: 1,
                        bgcolor: allNA ? '#fef2f2' : hasData ? '#f0fdf4' : '#f8fafc',
                        borderBottom: '1px solid #e2e8f0',
                    }}>
                        <Box sx={{
                            width: 8, height: 8, borderRadius: '50%',
                            bgcolor: allNA ? '#ef4444' : hasData ? '#22c55e' : '#94a3b8',
                        }} />
                        <Typography sx={{ fontFamily, fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>
                            {category.label}
                        </Typography>
                        <Typography sx={{ fontFamily, fontSize: '0.65rem', color: category.last_updated ? '#94a3b8' : '#ef4444', ml: 'auto', whiteSpace: 'nowrap' }}>
                            {category.last_updated ? new Date(category.last_updated).toLocaleString() : 'Never'}
                        </Typography>
                    </Box>
                    <Box sx={{ p: 1 }}>
                        {Object.entries(fields).map(([fKey, field]) => (
                            <DataFieldRow key={fKey} label={field.label} value={field.value} format={field.format} naReason={field.na_reason} />
                        ))}
                    </Box>
                </Paper>
            </Grid>
        );
    };

    // One pass over the shared sections; the trailing
    // `other` group carries title === null and renders
    // as a bare card grid, as it always has.
    return (
        <>
            {sections.map((section) => (
                <Box key={section.key} sx={{ mb: 2 }}>
                    {section.title && (
                        <Typography sx={{
                            fontFamily, fontWeight: 700, fontSize: '0.95rem',
                            color: '#1e293b', mb: 1.2,
                            borderLeft: `3px solid ${section.color}`, pl: 1,
                        }}>
                            {section.title}
                        </Typography>
                    )}
                    <Grid container spacing={2}>
                        {section.entries.map(renderCategoryCard)}
                    </Grid>
                </Box>
            ))}
        </>
    );
};
